import { useState, useEffect } from 'react';
import { axios } from '../axios';
import { formatDate } from '../utils/dateRangeUtils';

const BookingManagement = () => {
    const [bookings, setBookings] = useState([]);
    const [callbacks, setCallbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('bookings');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [bookingsRes, callbacksRes] = await Promise.all([
                axios.get('/api/bookings'),
                axios.get('/api/callbacks')
            ]);
            setBookings(bookingsRes.data.data || []);
            setCallbacks(callbacksRes.data.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateBookingStatus = async (bookingId, status) => {
        try {
            await axios.patch(`/api/booking/${bookingId}/status`, { status });
            fetchData();
        } catch (error) {
            console.error('Error updating booking status:', error);
        }
    };

    const deleteBooking = async (bookingId) => {
        if (window.confirm('Are you sure you want to delete this booking?')) {
            try {
                await axios.delete(`/api/booking/${bookingId}`);
                fetchData();
            } catch (error) {
                console.error('Error deleting booking:', error);
            }
        }
    };

    const deleteCallback = async (callbackId) => {
        if (window.confirm('Are you sure you want to delete this callback?')) {
            try {
                await axios.delete(`/api/callback/${callbackId}`);
                fetchData();
            } catch (error) {
                console.error('Error deleting callback:', error);
            }
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'confirmed': return 'badge bg-success';
            case 'cancelled': return 'badge bg-danger';
            default: return 'badge bg-warning';
        }
    };

    if (loading) {
        return <div className="d-flex justify-content-center p-5"><div className="spinner-border"></div></div>;
    }

    return (
        <div className="container-fluid p-4">
            <h2>Booking &amp; Callback Management</h2>

            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'bookings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('bookings')}
                    >
                        Bookings ({bookings.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'callbacks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('callbacks')}
                    >
                        Callbacks ({callbacks.length})
                    </button>
                </li>
            </ul>

            {activeTab === 'bookings' && (
                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Tour Type</th>
                                <th>Tour ID</th>
                                <th>Date</th>
                                <th>Guests</th>
                                <th>Total Price</th>
                                <th>Will you share with us?</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings.map(booking => (
                                <tr key={booking.id}>
                                    <td>{booking.id}</td>
                                    <td>{booking.name}</td>
                                    <td>{booking.email}</td>
                                    <td>{booking.phone}</td>
                                    <td className="text-capitalize">{booking.tour_type}</td>
                                    <td>{booking.tour_id}</td>
                                    <td>{formatDate(booking.date)}</td>
                                    <td>{booking.guests}</td>
                                    <td>${booking.total_price}</td>
                                    <td>
                                        <div style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                            {booking.message || '-'}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={getStatusBadgeClass(booking.status)}>
                                            {booking.status}
                                        </span>
                                    </td>
                                    <td>{formatDate(booking.created_at)}</td>
                                    <td>
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                className="btn btn-success btn-sm"
                                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                                disabled={booking.status === 'confirmed'}
                                            >
                                                Confirm
                                            </button>
                                            <button
                                                className="btn btn-warning btn-sm"
                                                onClick={() => updateBookingStatus(booking.id, 'pending')}
                                                disabled={booking.status === 'pending'}
                                            >
                                                Pending
                                            </button>
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                                disabled={booking.status === 'cancelled'}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => deleteBooking(booking.id)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {bookings.length === 0 && (
                        <div className="text-center p-4">
                            <p>No bookings found.</p>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'callbacks' && (
                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Will you share with us?</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {callbacks.map(callback => (
                                <tr key={callback.id}>
                                    <td>{callback.id}</td>
                                    <td>{callback.name}</td>
                                    <td>{callback.email}</td>
                                    <td>{callback.phone}</td>
                                    <td>
                                        <div style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                            {callback.message || 'No message'}
                                        </div>
                                    </td>
                                    <td>{formatDate(callback.created_at)}</td>
                                    <td>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => deleteCallback(callback.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {callbacks.length === 0 && (
                        <div className="text-center p-4">
                            <p>No callbacks found.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BookingManagement;
